import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlans,
  getTrialDays,
  createPlan,
  updatePlan,
  getUserSubscription,
  grantSubscription,
  getUserFuncionarios,
} from '@/api/endpoints/subscription';
import type { PlanoInput, GrantSubscriptionInput } from '@/types/subscription.types';

// ----- Planos -----

export const usePlans = () => {
  return useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

// Dias de trial configurados no servidor — pro modal de concessão manual
// (subscriptionModal) e qualquer outra tela mostrarem o valor real.
export const useTrialDays = () => {
  return useQuery({
    queryKey: ['trialDays'],
    queryFn: getTrialDays,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanoInput) => createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PlanoInput }) => updatePlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
};

// ----- Assinatura por usuário -----

export const useUserSubscription = (userId: string | number | null, enabled = true) => {
  return useQuery({
    queryKey: ['user-subscription', userId],
    queryFn: () => getUserSubscription(userId as string | number),
    staleTime: 1000 * 60,
    retry: 1,
    enabled: enabled && !!userId,
  });
};

export const useUserFuncionarios = (userId: string | number | null, enabled = true) => {
  return useQuery({
    queryKey: ['user-funcionarios', userId],
    queryFn: () => getUserFuncionarios(userId as string | number),
    staleTime: 1000 * 60,
    retry: 1,
    enabled: enabled && !!userId,
  });
};

// Grant simplificado (dar/promover): aplica um dos 6 tipos do seletor.
export const useGrantSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string | number; data: GrantSubscriptionInput }) =>
      grantSubscription(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-subscription', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
